import { oc } from "@orpc/contract";
import { z } from "zod";

// ─── Shared schemas ─────────────────────────────────────────────────────────

export const ProductMarginSalesByYearSchema = z.object({
  year: z.number().int(),
  /** Total quantity sold across all line items for this product in this year. */
  qtySold: z.number().int(),
  /** Quantity-weighted average sales price in USD (decimal string). */
  aspWeighted: z.string(),
});

export const ProductMarginRowSchema = z.object({
  productVariantId: z.string().uuid(),
  sku: z.string().nullable(),
  productName: z.string(),
  description: z.string().nullable(),
  /** Cost in USD as a decimal string (post-conversion if costCurrency is EUR). */
  costUsd: z.string().nullable(),
  /** Raw cost as stored in `origin_metadata.FROZEN_ITEM_COST`, decimal string. */
  costRaw: z.string().nullable(),
  /** The currency the raw cost is recorded in for the source org. */
  costCurrency: z.enum(["USD", "EUR"]),
  /** Target margin percent (e.g. "71.5" for 71.5%), decimal string. */
  targetMarginPct: z.string().nullable(),
  productFamily: z.string().nullable(),
  productGroup: z.string().nullable(),
  productModel: z.string().nullable(),
  /** Historical sales per year (descending by year). Empty when the product
   *  has no sales history in the queried window. */
  salesByYear: z.array(ProductMarginSalesByYearSchema),
});

const MetaSchema = {
  hasProductMargins: z.boolean(),
  costCurrency: z.enum(["USD", "EUR"]),
  conversionApplied: z.boolean(),
  /** Years included in this query's sales aggregation, descending.
   *  Frontend uses this to label year columns dynamically. */
  yearsCovered: z.array(z.number().int()),
} as const;

// ─── list ───────────────────────────────────────────────────────────────────

export const ListProductMarginsInputSchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  search: z.string().trim().optional(),
  family: z.string().trim().optional(),
  group: z.string().trim().optional(),
  model: z.string().trim().optional(),
});

export const ListProductMarginsResponseSchema = z.object({
  rows: z.array(ProductMarginRowSchema),
  total: z.number().int().min(0),
  ...MetaSchema,
});

// ─── byVariantIds ───────────────────────────────────────────────────────────

export const ProductMarginsByVariantIdsInputSchema = z.object({
  productVariantIds: z.array(z.string().uuid()).max(500),
  /** Scope the sales history (ASP / qty sold) to customers sharing this
   *  customer's price list — the distributor's "region". Omitted (or when the
   *  customer resolves to no price list) the history stays org-wide. */
  customerId: z.string().uuid().optional(),
});

export const ProductMarginsByVariantIdsResponseSchema = z.object({
  rows: z.array(ProductMarginRowSchema),
  ...MetaSchema,
  /** How the sales history was scoped: `price_list_peers` when a customerId
   *  resolved to a price list, `organization` otherwise. */
  salesScope: z.enum(["organization", "price_list_peers"]).optional(),
});

// ─── classifications ───────────────────────────────────────────────────────

export const ProductMarginClassificationSchema = z.object({
  family: z.string(),
  group: z.string(),
  model: z.string(),
});

export const ProductMarginClassificationsResponseSchema = z.object({
  classifications: z.array(ProductMarginClassificationSchema),
});

// ─── extractPricingFile / listPricingFiles ──────────────────────────────────

/** Document-association entityType stamped on uploaded pricing files. */
export const PRICING_FILE_ENTITY_TYPE = "product_margins_pricing_file";

export const ExtractPricingFileInputSchema = z.object({
  /** An uploaded document (presigned-upload flow) owned by the caller. */
  documentId: z.string().uuid(),
});

export const ExtractedPricingRowSchema = z.object({
  /** Product identifier as written in the document (SKU, item #, UPC…). */
  identifier: z.string().nullable(),
  /** Product name/description as written in the document. */
  description: z.string().nullable(),
  quantity: z.number().nullable(),
  unitPrice: z.number().nullable(),
});

export const ExtractPricingFileResponseSchema = z.object({
  rows: z.array(ExtractedPricingRowSchema),
});

export const PricingFileSchema = z.object({
  documentId: z.string().uuid(),
  fileName: z.string(),
  /** ISO timestamp of the upload. */
  uploadedAt: z.string(),
});

export const ListPricingFilesResponseSchema = z.object({
  files: z.array(PricingFileSchema),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const productMarginsContracts = {
  list: oc
    .input(ListProductMarginsInputSchema)
    .output(ListProductMarginsResponseSchema)
    .route({
      method: "GET",
      summary: "List product margins (paginated) for the current organization",
    }),
  byVariantIds: oc
    .input(ProductMarginsByVariantIdsInputSchema)
    .output(ProductMarginsByVariantIdsResponseSchema)
    .route({
      method: "POST",
      summary: "Bulk-load product margin rows by product variant id",
    }),
  classifications: oc.output(ProductMarginClassificationsResponseSchema).route({
    method: "GET",
    summary:
      "Distinct PRODVIEW (family, group, model) triples for the current org's products",
  }),
  extractPricingFile: oc
    .input(ExtractPricingFileInputSchema)
    .output(ExtractPricingFileResponseSchema)
    .route({
      method: "POST",
      summary:
        "AI-extract product / unit price / quantity line items from an uploaded pricing document",
    }),
  listPricingFiles: oc.output(ListPricingFilesResponseSchema).route({
    method: "GET",
    summary:
      "List the caller's previously uploaded pricing files, newest first",
  }),
} as const;

// ─── Type exports ───────────────────────────────────────────────────────────

export type ProductMarginRow = z.infer<typeof ProductMarginRowSchema>;
export type ListProductMarginsInput = z.infer<
  typeof ListProductMarginsInputSchema
>;
export type ListProductMarginsResponse = z.infer<
  typeof ListProductMarginsResponseSchema
>;
export type ProductMarginsByVariantIdsInput = z.infer<
  typeof ProductMarginsByVariantIdsInputSchema
>;
export type ProductMarginsByVariantIdsResponse = z.infer<
  typeof ProductMarginsByVariantIdsResponseSchema
>;
export type ProductMarginClassification = z.infer<
  typeof ProductMarginClassificationSchema
>;
export type ProductMarginClassificationsResponse = z.infer<
  typeof ProductMarginClassificationsResponseSchema
>;
export type ProductMarginSalesByYear = z.infer<
  typeof ProductMarginSalesByYearSchema
>;
export type ExtractPricingFileInput = z.infer<
  typeof ExtractPricingFileInputSchema
>;
export type ExtractedPricingRow = z.infer<typeof ExtractedPricingRowSchema>;
export type ExtractPricingFileResponse = z.infer<
  typeof ExtractPricingFileResponseSchema
>;
export type PricingFile = z.infer<typeof PricingFileSchema>;
export type ListPricingFilesResponse = z.infer<
  typeof ListPricingFilesResponseSchema
>;
